import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, ArrowsClockwise, CaretRight } from "@phosphor-icons/react";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLES = {
  critical: { text: "text-red-400", dot: "bg-red-400", bar: "bg-red-500" },
  high:     { text: "text-orange-400", dot: "bg-orange-400", bar: "bg-orange-500" },
  medium:   { text: "text-amber-400", dot: "bg-amber-400", bar: "bg-amber-500" },
  low:      { text: "text-emerald-400", dot: "bg-emerald-400", bar: "bg-emerald-500" },
};

export default function LiveRiskWidget({ onSelectDevice, limit = 8 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ai/fleet-risk`, { params: { limit } });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { fetch(); }, [fetch]);

  // Refetch on live event ingestion / alert (debounced)
  const refetchTimer = useRef(null);
  const handleLive = useCallback((msg) => {
    if (!["event.ingested", "alert.created"].includes(msg.type)) return;
    if (refetchTimer.current) return;
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null;
      fetch();
    }, 1500); // small debounce — risk score is computed server-side
  }, [fetch]);
  useLiveUpdates(handleLive);

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="live-risk-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1.5">
          <Shield size={12} weight="duotone" className="text-teal-400" />
          Live Risk
        </h3>
        <div className="flex items-center gap-2">
          {data && <Badge variant="outline" className="border-zinc-700 text-[10px] font-mono">Top {data.items?.length || 0}</Badge>}
          <button onClick={fetch} className="text-zinc-500 hover:text-teal-400 transition-colors" title="Refresh"
            data-testid="live-risk-refresh">
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs font-mono text-zinc-500 animate-pulse text-center py-6">Calculating risk scores...</div>
      ) : !data || !data.items?.length ? (
        <div className="text-center py-6">
          <Shield size={28} weight="duotone" className="text-emerald-400 mx-auto mb-2" />
          <p className="text-xs text-zinc-500">No at-risk devices</p>
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="live-risk-list">
          {data.items.map((d) => {
            const style = LEVEL_STYLES[d.risk_level] || LEVEL_STYLES.low;
            const clickable = !!d.dataset_id && !!onSelectDevice;
            return (
              <div key={d.device_name}
                onClick={() => clickable && onSelectDevice(d.device_name, d.dataset_id)}
                className={`group flex items-center gap-3 p-2 rounded-sm bg-zinc-950/50 transition-colors ${clickable ? "hover:bg-zinc-800 cursor-pointer" : ""}`}
                data-testid={`live-risk-item-${d.device_name}`}>
                {/* Score */}
                <div className={`w-9 h-9 rounded-sm flex flex-col items-center justify-center shrink-0 bg-zinc-900 border border-zinc-800`}>
                  <span className={`text-sm font-mono font-bold leading-none ${style.text}`}>{d.risk_score}</span>
                  <span className="text-[7px] text-zinc-600 mt-0.5 uppercase tracking-wider">{d.risk_level}</span>
                </div>
                {/* Name + reason */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono font-semibold text-zinc-200 truncate">{d.device_name}</p>
                  <p className="text-[10px] text-zinc-500 truncate">{d.top_reason}</p>
                </div>
                {/* Bar */}
                <div className="hidden sm:block w-12 h-1 bg-zinc-800 rounded-full overflow-hidden shrink-0">
                  <div className={`h-full ${style.bar}`} style={{ width: `${d.risk_score}%` }} />
                </div>
                {clickable && <CaretRight size={11} className="text-zinc-700 group-hover:text-teal-400 transition-colors shrink-0" />}
              </div>
            );
          })}
        </div>
      )}

      {data?.calculated_at && (
        <p className="text-[9px] font-mono text-zinc-600 text-right mt-3 pt-2 border-t border-zinc-800">
          Updated {new Date(data.calculated_at).toLocaleTimeString()}
        </p>
      )}
    </Card>
  );
}
